import React, { useState, useEffect, useCallback } from 'react';
import { APP_VERSION } from './version';
import { api } from './api';
import { Doughnut } from 'react-chartjs-2';
import { Chart, registerables } from 'chart.js';
import { useParams, useNavigate } from 'react-router-dom';
import './PlayerSearch.css';

const BUILD_DATE = process.env.REACT_APP_BUILD_DATE || '';

Chart.register(...registerables);

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  if (/^\d{2}\.\d{2}\.\d{4}$/.test(dateStr)) return dateStr;
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  } catch (e) {
    return dateStr;
  }
};

const getEventTime = (detail) => {
  if (detail.event_epoch != null && detail.event_epoch !== '') {
    const n = Number(detail.event_epoch);
    if (!Number.isNaN(n)) return n < 1e12 ? n * 1000 : n;
  }
  const s = (detail.event_date || '').toString().trim();
  if (!s) return 0;
  const iso = /^\d{4}-\d{2}-\d{2}/.test(s);
  if (iso) {
    const t = new Date(s).getTime();
    if (!Number.isNaN(t)) return t;
  }
  const dmy = s.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (dmy) {
    const [, day, month, year] = dmy;
    const t = new Date(Number(year), Number(month) - 1, Number(day)).getTime();
    if (!Number.isNaN(t)) return t;
  }
  const ymd = s.match(/^(\d{4})\.(\d{1,2})\.(\d{1,2})$/);
  if (ymd) {
    const [, year, month, day] = ymd;
    const t = new Date(Number(year), Number(month) - 1, Number(day)).getTime();
    if (!Number.isNaN(t)) return t;
  }
  const t = new Date(s).getTime();
  return Number.isNaN(t) ? 0 : t;
};

const normalizePlayerName = (name) =>
  String(name ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ');

/** Read username/group from JWT payload (display only; not verified client-side). */
function parseJwtPayload(token) {
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;
    const json = atob(base64Url.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json);
  } catch {
    return null;
  }
}

/** Shareable URL for the current player page (works even when address bar is still `/`). */
function buildShareablePageUrl({ userNameEncoded, isPlayerSelected, playerStats, searchTerm }) {
  let playerName = '';
  if (userNameEncoded) {
    try {
      playerName = decodeURIComponent(userNameEncoded).trim();
    } catch {
      playerName = String(userNameEncoded).trim();
    }
  } else if (isPlayerSelected) {
    playerName = (playerStats.playerName || searchTerm || '').trim();
  }
  if (playerName) {
    return `${window.location.origin}/${encodeURIComponent(playerName)}`;
  }
  return window.location.href;
}

const PlayerSearch = () => {
  const [updateInfo, setUpdateInfo] = useState({ match_count: null, last_update: null });
  const [viewMode, setViewMode] = useState('table');
  const { userNameEncoded } = useParams();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [playerNames, setPlayerNames] = useState([]);
  const [selectedName, setSelectedName] = useState('');
  const [playerDetails, setPlayerDetails] = useState([]);
  const [playerStats, setPlayerStats] = useState({
    wins: 0,
    losses: 0,
    total_matches: 0,
    playerName: '',
    details: { Labels: [], datasets: [{ data: [] }] },
  });
  const [isPlayerSelected, setIsPlayerSelected] = useState(false);
  const [authUser, setAuthUser] = useState(null);
  const [resultsUser, setResultsUser] = useState(null);
  const [elo, setElo] = useState(null);
  const [loginUsername, setLoginUsername] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [copyLinkCopied, setCopyLinkCopied] = useState(false);

  // Re-sync from localStorage on mount and when the player URL changes (SPA navigation).
  useEffect(() => {
    const t = localStorage.getItem('access_token');
    if (!t) {
      setAuthUser(null);
      return;
    }
    const p = parseJwtPayload(t);
    if (p?.sub && p?.group) {
      setAuthUser({ username: p.sub, group: p.group });
    } else {
      setAuthUser(null);
    }
  }, [userNameEncoded]);

  useEffect(() => {
    const url = '/updated';
    api.get(url)
      .then(response => {
        const d = response?.data || {};
        const matchCount = d.match_count ?? d.matchCount;
        const lastUpdate = d.last_update ?? d.lastUpdate ?? d.date;
        setUpdateInfo({
          match_count: typeof matchCount === 'number' ? matchCount : (matchCount != null ? Number(matchCount) : null),
          last_update: lastUpdate != null && lastUpdate !== '' ? String(lastUpdate) : null,
        });
      })
      .catch(() => {
        setUpdateInfo({ match_count: null, last_update: null });
      });
  }, []);

  const resolveCanonicalName = useCallback(async (name) => {
    try {
      const q = (name || '').trim().toLowerCase();
      if (!q) return name;
      const resp = await api.get(`/player_names?player_name=${encodeURIComponent(q)}`);
      const names = Array.isArray(resp.data?.player_names) ? resp.data.player_names : [];
      const exact = names.find((p) => String(p.name || p).toLowerCase() === q);
      return (exact && (exact.name || exact)) || (names[0] && (names[0].name || names[0])) || name;
    } catch (e) {
      return name;
    }
  }, []);

  const fetchPlayerData = useCallback(async (name) => {
    try {
      const response = await api.get(`/results?player_name=${encodeURIComponent(name || '')}`);
      setPlayerDetails(response.data.results || []);
      setResultsUser(response.data.user ?? null);
      setElo(response.data.elo ?? null);
      setPlayerStats(prev => ({
        ...prev,
        wins: response.data.player_stats.wins,
        losses: response.data.player_stats.losses,
        total_matches: response.data.player_stats.total_matches,
        playerName: response.data.player_stats.playerName,
        details: response.data.player_stats.details,
      }));
      setIsPlayerSelected(true);
    } catch (error) {
      console.error("Failed to fetch player data:", error);
    }
  }, []);

  const handleLogin = async (event) => {
    event.preventDefault();
    setLoginError('');
    try {
      const r = await api.post('/auth/login', {
        username: loginUsername.trim(),
        password: loginPassword,
      });
      localStorage.setItem('access_token', r.data.access_token);
      setLoginPassword('');
      if (r.data.user) {
        setAuthUser(r.data.user);
      }
      // Refetch current player so /results includes `user` (Bearer is now in localStorage for `api`).
      if (userNameEncoded) {
        const decodedName = decodeURIComponent(userNameEncoded);
        const canonical = await resolveCanonicalName(decodedName.trim());
        await fetchPlayerData(canonical);
      } else if (isPlayerSelected && searchTerm.trim()) {
        const canonical = await resolveCanonicalName(searchTerm.trim());
        await fetchPlayerData(canonical);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setLoginError(typeof detail === 'string' ? detail : 'Login failed');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    setAuthUser(null);
    setResultsUser(null);
  };

  const handleCopyPageLink = async () => {
    const url = buildShareablePageUrl({
      userNameEncoded,
      isPlayerSelected,
      playerStats,
      searchTerm,
    });
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = url;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'absolute';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }
    setCopyLinkCopied(true);
    window.setTimeout(() => setCopyLinkCopied(false), 2000);
  };

  const showAdminBadge =
    resultsUser?.group === 'admin' || authUser?.group === 'admin';

  useEffect(() => {
    const run = async () => {
      if (userNameEncoded) {
        const decodedName = decodeURIComponent(userNameEncoded);
        const normalizedName = decodedName.trim();
        const canonical = await resolveCanonicalName(normalizedName);
        setSearchTerm(canonical);
        fetchPlayerData(canonical);
      }
    };
    run();
  }, [userNameEncoded, resolveCanonicalName, fetchPlayerData]);

  const handleSearch = async (event) => {
    const { value } = event.target;
    const searchTermLowerCase = value.toLowerCase();

    setSearchTerm(value);

    if (searchTermLowerCase.length >= 3) {
      try {
        const response = await api.get(
          `/player_names?player_name=${encodeURIComponent(searchTermLowerCase)}`
        );
        const playerNames = response.data.player_names;
        setPlayerNames(playerNames);
      } catch (error) {
        console.error(error);
      }

      setIsPlayerSelected(false);
    } else {
      setPlayerNames([]);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      const exactMatch = playerNames.find(
        (p) => p.name.toLowerCase() === searchTerm.toLowerCase()
      );
      if (exactMatch) {
        handleNameClick(exactMatch.name);
      } else if (playerNames.length === 1) {
        handleNameClick(playerNames[0].name);
      }
    }
  };

  const handleNameClick = (name) => {
    setSearchTerm(name);
    setSelectedName(name);
    setPlayerNames([]);
    setIsPlayerSelected(true);
  };

  const handlePlayerClick = (name) => {
    setSearchTerm(name);
    setSelectedName(name);
    setPlayerNames([]);
    setIsPlayerSelected(true);
  };

  const handlePlayerLink = (name) => {
    const encoded = encodeURIComponent(name);
    navigate(`/${encoded}`);
  };

  const getExternalLink = (detail) => {
    if (detail.event_link && detail.event_link.includes('smoothcomp')) {
      return detail.event_link;
    }
    if (detail.link && detail.link.includes('smoothcomp')) {
      return detail.link;
    }
    if (detail.serie_link) {
      return detail.serie_link;
    }
    return detail.event_link || '#';
  };

  const getSeriesInfo = (detail) => {
    if (detail.series1 || detail.series2) {
      const series = [detail.series1, detail.series2].filter(s => s && s.trim()).join(' / ');
      return series;
    }
    return null;
  };

  const getResultType = (detail) =>
    (detail.wonby ?? detail.match_result ?? '').toString().trim() || null;

  const generateRandomColors = useCallback((count, labels) => {
    const colors = [];
    for (let i = 0; i < count; i++) {
      let color = '';
      if (labels[i] === 'Injury') {
        color = '#ef4444';
      } else if (labels[i] === 'Points') {
        color = '#22c55e';
      } else if (labels[i] === 'Submission') {
        color = '#f59e0b';
      } else if (labels[i] === 'Decision') {
        color = '#3b82f6';
      } else if (labels[i] === 'Walkover') {
        color = '#8b5cf6';
      } else {
        const hue = (i * 137.508) % 360;
        color = `hsla(${hue}, 70%, 55%, 0.85)`;
      }
      colors.push(color);
    }
    return colors;
  }, []);

  const sortedMatches = React.useMemo(() => {
    if (!Array.isArray(playerDetails) || playerDetails.length === 0) return [];
    return [...playerDetails].sort((a, b) => getEventTime(b) - getEventTime(a));
  }, [playerDetails]);

  const wonbyChartData = React.useMemo(() => {
    if (!Array.isArray(playerDetails) || playerDetails.length === 0) return { labels: [], data: [], backgroundColor: [] };
    const countBy = {};
    for (const d of playerDetails) {
      const type = getResultType(d);
      if (!type) continue;
      countBy[type] = (countBy[type] || 0) + 1;
    }
    const sorted = Object.entries(countBy).sort((a, b) => b[1] - a[1]);
    const labels = sorted.map(([k]) => k);
    const data = sorted.map(([, v]) => v);
    const backgroundColor = generateRandomColors(data.length, labels);
    return { labels, data, backgroundColor };
  }, [playerDetails, generateRandomColors]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '65%',
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          boxWidth: 12,
          padding: 16,
          usePointStyle: true,
          pointStyle: 'circle',
          font: {
            size: 13,
            family: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
          }
        }
      }
    },
    animation: {
      animateRotate: true,
      animateScale: true,
      duration: 1000,
      easing: 'easeOutQuart'
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (isPlayerSelected) {
        try {
          const response = await api.get(
            `/results?player_name=${encodeURIComponent(searchTerm || '')}`
          );
          // Must match fetchPlayerData — backend only echoes `user` when Authorization Bearer is sent.
          setResultsUser(response.data.user ?? null);
          setElo(response.data.elo ?? null);
          const playerDetailsData = response.data.results;
          const { wins, losses, total_matches, details, playerName } = response.data.player_stats;

          setPlayerStats(prevStats => ({
            wins: wins || 0,
            losses: losses || 0,
            total_matches: total_matches || 0,
            playerName: playerName || searchTerm,
            details: {
              Labels: details.Labels || [],
              datasets: [
                {
                  data: details.datasets[0]?.data || [],
                  backgroundColor: generateRandomColors(
                    details.datasets[0]?.data?.length || 0,
                    details.Labels || []
                  ),
                },
              ],
            },
          }));
          setPlayerDetails(playerDetailsData);
        } catch (error) {
          console.error(error);
          setResultsUser(null);
          setPlayerDetails([]);
          setPlayerStats({
            wins: 0,
            losses: 0,
            total_matches: 0,
            playerName: searchTerm,
            details: { Labels: [], datasets: [{ data: [] }] },
          });
        }
      }
    };

    fetchData();
  }, [isPlayerSelected, searchTerm, generateRandomColors]);

  const winRate = playerStats.total_matches > 0
    ? Math.round((playerStats.wins / playerStats.total_matches) * 100)
    : 0;

  const playerName = playerStats.playerName;
  const details = playerStats.details || {};
  const labels = details.Labels || [];
  const detailsData = details.datasets?.[0]?.data || [];

  let winsSubmission = labels.includes('Submission') ? (detailsData[labels.indexOf('Submission')] || 0) : 0;
  let winsPoints = labels.includes('Points') ? (detailsData[labels.indexOf('Points')] || 0) : 0;
  let lossesSubmission = 0;
  let lossesPoints = 0;

  if (playerName && Array.isArray(playerDetails) && playerDetails.length > 0) {
    const normalizedPlayer = normalizePlayerName(playerName);
    winsSubmission = 0;
    winsPoints = 0;
    lossesSubmission = 0;
    lossesPoints = 0;
    for (const d of playerDetails) {
      const w = (getResultType(d) || '').toString().toLowerCase();
      const isSub = w.includes('submission');
      if (normalizePlayerName(d.match_winner) === normalizedPlayer) {
        if (isSub) winsSubmission += 1;
        else winsPoints += 1;
      } else if (normalizePlayerName(d.match_loser) === normalizedPlayer) {
        if (isSub) lossesSubmission += 1;
        else lossesPoints += 1;
      }
    }
  }

  const getResultIcon = (result) => {
    if (result?.includes('submission')) return '🔒';
    if (result?.includes('points')) return '⚡';
    if (result?.includes('decision')) return '⚖️';
    if (result?.includes('walkover')) return '🏆';
    if (result?.includes('injury')) return '⚠️';
    return '📊';
  };

  return (
    <div className="app-container">
      <div className="bg-effects">
        <div className="bg-orb bg-orb-1"></div>
        <div className="bg-orb bg-orb-2"></div>
        <div className="bg-orb bg-orb-3"></div>
      </div>

      <div className="main-content">
        <header className="hero-section">
          <div className="logo">
            <button type="button" className="back-btn" onClick={() => navigate(-1)} title="Go back">&#8592;</button>
            <span className="logo-icon">🥋</span>
            <h1>BJJ Search</h1>
          </div>
          <p className="tagline">Track fighters, analyze results, discover champions</p>
          <div className="auth-bar">
            {authUser ? (
              <>
                <span className="auth-user-label">{authUser.username}</span>
                <button type="button" className="auth-logout-btn" onClick={handleLogout}>
                  Log out
                </button>
              </>
            ) : (
              <form className="auth-form" onSubmit={handleLogin}>
                <input
                  type="text"
                  autoComplete="username"
                  placeholder="Username"
                  value={loginUsername}
                  onChange={(e) => setLoginUsername(e.target.value)}
                  className="auth-input"
                />
                <input
                  type="password"
                  autoComplete="current-password"
                  placeholder="Password"
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  className="auth-input"
                />
                <button type="submit" className="auth-login-btn">
                  Log in
                </button>
              </form>
            )}
            {loginError ? <span className="auth-error">{loginError}</span> : null}
          </div>
        </header>

        <section className="search-section">
          <div className="search-container">
            <div className="search-row">
              <div className="search-input-wrapper">
                <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"/>
                  <path d="M21 21l-4.35-4.35"/>
                </svg>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={handleSearch}
                  onKeyDown={handleKeyDown}
                  placeholder="Search for a fighter..."
                  className="search-input"
                />
                {searchTerm && (
                  <button type="button" className="clear-btn" onClick={() => { setSearchTerm(''); setPlayerNames([]); }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M18 6L6 18M6 6l12 12"/>
                    </svg>
                  </button>
                )}
              </div>
              <button
                type="button"
                className={`copy-link-btn${copyLinkCopied ? ' copy-link-btn--copied' : ''}`}
                onClick={handleCopyPageLink}
                title="Copy page link"
                aria-label="Copy page link to clipboard"
              >
                {copyLinkCopied ? (
                  <span className="copy-link-btn-label">Copied!</span>
                ) : (
                  <>
                    <svg className="copy-link-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                    </svg>
                    <span className="copy-link-btn-label">Copy link</span>
                  </>
                )}
              </button>
            </div>
            {playerNames.length > 0 && (
              <ul className="search-dropdown">
                {playerNames.map((p) => (
                  <li
                    key={p.name}
                    className={`dropdown-item ${selectedName === p.name ? 'selected' : ''}`}
                    onClick={() => handleNameClick(p.name)}
                  >
                    <span className="item-icon">👤</span>
                    <span className="item-name">{p.name}</span>
                    {showAdminBadge && p.elo && (
                      <span className="item-elo">{p.elo.all}/{p.elo.gi}/{p.elo.nogi}</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
            <a href="/top-50" className="admin-menu-link" style={{ marginTop: 8 }}>
              🏆 Top 50 Players
            </a>
          </div>
        </section>

        {isPlayerSelected && playerStats && playerStats.playerName && (
          <section className="results-section">
            <div className="results-container">
              <div className="profile-card">
                <div className="profile-header">
                  <div className="profile-avatar">
                    {playerStats.playerName.charAt(0).toUpperCase()}
                  </div>
                  <div className="profile-info">
                    <h2 className="profile-name">{playerStats.playerName}</h2>
                    <div className="profile-stats">
                      <div className="stat-badge wins">
                        <span className="stat-value">{playerStats.wins}</span>
                        <span className="stat-label">Wins</span>
                      </div>
                      <div className="stat-badge losses">
                        <span className="stat-value">{playerStats.losses}</span>
                        <span className="stat-label">Losses</span>
                      </div>
                      <div className="stat-badge total">
                        <span className="stat-value">{playerStats.total_matches}</span>
                        <span className="stat-label">Matches</span>
                      </div>
                    </div>
                  </div>
                  <div className="win-rate-indicator">
                    <svg viewBox="0 0 36 36" className="win-rate-ring">
                      <path
                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                        fill="none"
                        stroke="#3d3d3d"
                        strokeWidth="3"
                      />
                      <path
                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                        fill="none"
                        stroke="url(#winRateGradient)"
                        strokeWidth="3"
                        strokeDasharray={`${winRate}, 100`}
                      />
                      <defs>
                        <linearGradient id="winRateGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                          <stop offset="0%" stopColor="#22c55e" />
                          <stop offset="100%" stopColor="#4ade80" />
                        </linearGradient>
                      </defs>
                    </svg>
                    <span className="win-rate-value">{winRate}%</span>
                    <span className="win-rate-label">Win Rate</span>
                  </div>
                  <div className="submission-breakdown">
                    <div className="submission-chart">
                      <Doughnut
                        data={{
                          labels: ['Submission', 'Points'],
                          datasets: [{
                            data: [winsSubmission, winsPoints],
                            backgroundColor: ['#f59e0b', '#22c55e'],
                            borderWidth: 0,
                          }],
                        }}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          cutout: '65%',
                          plugins: { legend: { display: false } }
                        }}
                      />
                      <div className="submission-label">
                        <span className="submission-rate">{Math.round((winsSubmission / (winsSubmission + winsPoints)) * 100) || 0}%</span>
                        <span className="submission-type">Wins Sub%</span>
                      </div>
                    </div>
                    <div className="submission-chart">
                      <Doughnut
                        data={{
                          labels: ['Submission', 'Points'],
                          datasets: [{
                            data: [lossesSubmission, lossesPoints],
                            backgroundColor: ['#f59e0b', '#ef4444'],
                            borderWidth: 0,
                          }],
                        }}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          cutout: '65%',
                          plugins: { legend: { display: false } }
                        }}
                      />
                      <div className="submission-label">
                        <span className="submission-rate losses">{lossesSubmission + lossesPoints > 0 ? Math.round((lossesSubmission / (lossesSubmission + lossesPoints)) * 100) : 0}%</span>
                        <span className="submission-type">Losses Sub%</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {showAdminBadge && elo && (
                <div className="elo-box">
                  <h3 className="elo-box-title">
                    <span className="title-icon">⚡</span>
                    ELO Ratings
                  </h3>
                  <div className="elo-box-stats">
                    <div className="elo-stat">
                      <span className="elo-stat-value">{elo.all_elo}</span>
                      <span className="elo-stat-label">All ({elo.all_matches} matches)</span>
                    </div>
                    <div className="elo-stat">
                      <span className="elo-stat-value">{elo.gi_elo}</span>
                      <span className="elo-stat-label">Gi ({elo.gi_matches} matches)</span>
                    </div>
                    <div className="elo-stat">
                      <span className="elo-stat-value">{elo.nogi_elo}</span>
                      <span className="elo-stat-label">No-Gi ({elo.nogi_matches} matches)</span>
                    </div>
                  </div>
                </div>
              )}

              <div className={`content-grid ${viewMode === 'table' ? 'full-width' : ''}`}>
                <div className="chart-card">
                  <h3 className="card-title">
                    <span className="title-icon">📈</span>
                    Match Results
                  </h3>
                  <div className="chart-wrapper">
                    {wonbyChartData.labels.length > 0 ? (
                      <Doughnut
                        data={{
                          labels: wonbyChartData.labels,
                          datasets: [{
                            data: wonbyChartData.data,
                            backgroundColor: wonbyChartData.backgroundColor,
                            borderWidth: 0,
                            hoverOffset: 8
                          }],
                        }}
                        options={chartOptions}
                      />
                    ) : (
                      <div className="no-data">No match data available</div>
                    )}
                  </div>
                  <div className="result-legend">
                    {wonbyChartData.labels.map((label, idx) => (
                      <div key={label} className="legend-item">
                        <span className="legend-color" style={{ backgroundColor: wonbyChartData.backgroundColor[idx] }}></span>
                        <span className="legend-label">{label}</span>
                        <span className="legend-value">{wonbyChartData.data[idx] || 0}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="matches-card" style={{ gridColumn: viewMode === 'table' ? '1 / -1' : 'auto' }}>
                  <div className="matches-header">
                    <h3 className="card-title">
                      <span className="title-icon">📋</span>
                      Recent Matches
                    </h3>
                    <div className="view-toggle">
                      <button
                        className={`toggle-btn ${viewMode === 'normal' ? 'active' : ''}`}
                        onClick={() => setViewMode('normal')}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="3" y="3" width="7" height="7" rx="1"/>
                          <rect x="14" y="3" width="7" height="7" rx="1"/>
                          <rect x="3" y="14" width="7" height="7" rx="1"/>
                          <rect x="14" y="14" width="7" height="7" rx="1"/>
                        </svg>
                        Cards
                      </button>
                      <button
                        className={`toggle-btn ${viewMode === 'table' ? 'active' : ''}`}
                        onClick={() => setViewMode('table')}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="3" y="3" width="18" height="18" rx="2"/>
                          <path d="M3 9h18M3 15h18M9 3v18"/>
                        </svg>
                        Table
                      </button>

                    </div>
                  </div>
                  {viewMode === 'normal' ? (
                    <div className="matches-list">
                      {sortedMatches.map((detail, index) => (
                        <div key={index} className="match-item">
                          <div className="match-date">{formatDate(detail.event_date)}</div>
                          <div className="match-content">
                            <div className="match-event">
                              <a href={getExternalLink(detail)} target="_blank" rel="noopener noreferrer" className="event-link">
                                {detail.event_name}
                              </a>
                              {detail.link ? (
                                <a href={detail.link} target="_blank" rel="noopener noreferrer" className="weight-class bracket-link">
                                  {detail.weight_class}
                                </a>
                              ) : (
                                <span className="weight-class">{detail.weight_class}</span>
                              )}
                            </div>
                            <div className="match-details">
                              <div className="players-row">
                                <button className="player-link" onClick={() => handlePlayerLink(detail.match_winner)}>
                                  {detail.match_winner}
                                </button>
                                <span className="vs">vs</span>
                                <button className="player-link" onClick={() => handlePlayerLink(detail.match_loser === playerStats.playerName ? detail.match_winner : detail.match_loser)}>
                                  {detail.match_loser === playerStats.playerName ? detail.match_winner : detail.match_loser}
                                </button>
                              </div>
                              <div className="result-row">
                                <span className="result-icon">{getResultIcon(getResultType(detail))}</span>
                                <span className="result-text">{getResultType(detail) || '-'}</span>
                                {detail.gi && (
                                  <span className={`match-type-badge ${(detail.gi || '').toLowerCase().includes('no') ? 'match-type-nogi' : 'match-type-gi'}`}>{detail.gi}</span>
                                )}
                                {getSeriesInfo(detail) && (
                                  <span className="series-info">
                                    📋 {getSeriesInfo(detail)}
                                  </span>
                                )}
                                {detail.serie_winner != null && String(detail.serie_winner).trim() !== '' && (
                                  <span className={`series-winner ${String(detail.serie_winner).trim() === playerStats.playerName ? 'current-player' : ''}`}>
                                    🏆 <button className="player-link" onClick={() => handlePlayerLink(String(detail.serie_winner).trim())}>{String(detail.serie_winner).trim()}</button>
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="match-actions">
                            <button className="action-btn" onClick={() => handlePlayerClick(detail.match_winner)} title="View winner">
                              👤
                            </button>
                            <button className="action-btn" onClick={() => handlePlayerClick(detail.match_loser === playerStats.playerName ? detail.match_winner : detail.match_loser)} title="View opponent">
                              🥋
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="matches-table-wrapper">
                      <table className="matches-table">
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Event</th>
                            <th>Weight</th>
                            <th>Type</th>
                            <th>Opponent</th>
                            <th>Winner</th>
                            <th>Result</th>
                            <th>Series Winner</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortedMatches.map((detail, index) => {
                            const opponent = normalizePlayerName(detail.match_winner) === normalizePlayerName(playerStats.playerName) ? detail.match_loser : detail.match_winner;
                            return (
                            <tr key={index}>
                              <td className="table-date">{formatDate(detail.event_date)}</td>
                              <td className="table-event">
                                <a href={getExternalLink(detail)} target="_blank" rel="noopener noreferrer">
                                  {detail.event_name}
                                </a>
                              </td>
                              <td className="table-weight">
                                {detail.link ? (
                                  <a href={detail.link} target="_blank" rel="noopener noreferrer" className="bracket-link">
                                    {detail.weight_class}
                                  </a>
                                ) : (
                                  detail.weight_class
                                )}
                              </td>
                              <td className={`table-type ${(detail.gi || '').toLowerCase().includes('no') ? 'type-nogi' : 'type-gi'}`}>
                                {detail.gi || '-'}
                              </td>
                              <td className="table-player">
                                <button className="player-link" onClick={() => handlePlayerLink(opponent)}>
                                  {opponent}
                                </button>
                              </td>
                              <td className="table-player winner">
                                <button className="player-link" onClick={() => handlePlayerLink(detail.match_winner)}>
                                  {detail.match_winner}
                                </button>
                              </td>
                              <td className="table-result">
                                <span className={`result-badge ${(getResultType(detail) || '').toLowerCase().replace(/\s+/g, '-')}`}>
                                  {getResultIcon(getResultType(detail))} {getResultType(detail) || '-'}
                                </span>
                              </td>
                              <td className="table-series">
                                {detail.serie_winner != null && String(detail.serie_winner).trim() !== '' ? (
                                  <button className="player-link" onClick={() => handlePlayerLink(String(detail.serie_winner).trim())}>
                                    🏆 {String(detail.serie_winner).trim()}
                                  </button>
                                ) : (
                                  <span className="no-series">-</span>
                                )}
                              </td>
                            </tr>
                          );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>
        )}

        {!isPlayerSelected && !searchTerm && (
          <section className="empty-state">
            <div className="empty-icon">🥋</div>
            <h2>Find Your Fighter</h2>
            <p>Search for any BJJ competitor to see their competition history, stats, and results</p>
            <div className="feature-list">
              <div className="feature-item">
                <span className="feature-icon">📊</span>
                <span>Detailed match statistics</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon">🏆</span>
                <span>Competition results</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon">👥</span>
                <span>Opponent tracking</span>
              </div>
            </div>
          </section>
        )}

        <footer className="footer">
          <span className="version-badge">
            Matches: {updateInfo.match_count != null ? updateInfo.match_count.toLocaleString() : '—'}, Last update {updateInfo.last_update ? formatDate(updateInfo.last_update) : '—'}, Build {BUILD_DATE || '—'}, {APP_VERSION}
          </span>
        </footer>
      </div>
    </div>
  );
};

export default PlayerSearch;